import * as React from "react";
import { ChevronDown, ChevronRight, Sparkles, User, Wrench } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ChatMessage, ChatToolBlock } from "@/lib/types";
import { dollars } from "@/lib/money";

export function ChatMessageView({ message }: { message: ChatMessage }) {
  const isUser = message.role === "user";
  return (
    <div className={cn("flex gap-2", isUser && "justify-end")}>
      {!isUser ? (
        <div className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-md bg-brand/15 text-brand">
          <Sparkles className="h-3 w-3" />
        </div>
      ) : null}
      <div
        className={cn(
          "min-w-0 max-w-[85%] rounded-md px-3 py-2 text-sm",
          isUser
            ? "bg-brand/15 text-foreground ring-1 ring-inset ring-brand/30"
            : "bg-card/60 text-foreground/95 ring-1 ring-inset ring-border",
        )}
      >
        {message.tool_blocks?.length
          ? message.tool_blocks.map((b) => <ToolBlockView key={b.id} block={b} />)
          : null}
        <div className="whitespace-pre-wrap break-words leading-relaxed">
          {message.content || (
            message.streaming ? (
              <span className="inline-flex items-center gap-1 text-muted-foreground">
                <span className="h-1 w-1 animate-pulse-soft rounded-full bg-brand" />
                <span className="h-1 w-1 animate-pulse-soft rounded-full bg-brand [animation-delay:120ms]" />
                <span className="h-1 w-1 animate-pulse-soft rounded-full bg-brand [animation-delay:240ms]" />
              </span>
            ) : null
          )}
        </div>
        {typeof message.cost_cents === "number" && message.cost_cents > 0 ? (
          <div className="mt-1.5 text-2xs text-muted-foreground">
            cost {dollars(message.cost_cents)}
          </div>
        ) : null}
      </div>
      {isUser ? (
        <div className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-md bg-muted text-muted-foreground">
          <User className="h-3 w-3" />
        </div>
      ) : null}
    </div>
  );
}

function ToolBlockView({ block }: { block: ChatToolBlock }) {
  const [open, setOpen] = React.useState(false);
  const tone =
    block.state === "error"
      ? "border-destructive/40 bg-destructive/10"
      : block.state === "running"
        ? "border-warning/40 bg-warning/10"
        : "border-border bg-muted/40";
  return (
    <div className={cn("mb-2 overflow-hidden rounded-md border text-xs", tone)}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-1.5 px-2 py-1.5 text-left"
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        <Wrench className="h-3 w-3 opacity-70" />
        <span className="font-mono font-medium">{block.name}</span>
        <span className="ml-auto text-2xs text-muted-foreground">
          {block.state === "running"
            ? "running…"
            : block.state === "error"
              ? "error"
              : "done"}
        </span>
      </button>
      {open ? (
        <div className="space-y-1 border-t border-border/60 p-2 font-mono text-2xs">
          <div className="text-muted-foreground">input</div>
          <pre className="overflow-x-auto whitespace-pre-wrap break-words text-foreground/80">
            {JSON.stringify(block.input, null, 2)}
          </pre>
          {block.output !== undefined ? (
            <>
              <div className="mt-1.5 text-muted-foreground">output</div>
              <pre className="overflow-x-auto whitespace-pre-wrap break-words text-foreground/80">
                {typeof block.output === "string"
                  ? block.output
                  : JSON.stringify(block.output, null, 2)}
              </pre>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
